import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { updateAccountPreferences } from '@/lib/api'
import { normalizeDiscoveryDefaults } from '@/lib/screenerDiscoveryDefaults'
import type { ScreenerDiscoveryFilters, User } from '@/types'

/** Account OI rules are separate from exchange selection and transient search. */
export function useScreenerDiscoveryDefaults(
  user: User | null,
  filters: ScreenerDiscoveryFilters,
  setFilters: Dispatch<SetStateAction<ScreenerDiscoveryFilters>>,
  hydrated: boolean,
) {
  const client = useQueryClient()
  const username = user?.username ?? null
  const currentUser = useRef(username)
  currentUser.current = username
  const restoredUser = useRef<string | null>(null)
  const request = useRef(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const saved = useMemo(() => normalizeDiscoveryDefaults(user?.preferences?.screener_discovery_filters),
    [user?.preferences?.screener_discovery_filters])

  useEffect(() => {
    restoredUser.current = null
    request.current += 1
    setSaving(false)
    setError(null)
  }, [username])

  const restore = useCallback(() => {
    if (!saved) return
    setFilters((current) => ({
      ...current, ...saved,
      minMatchingExchanges: Math.min(saved.minMatchingExchanges, Math.max(1, current.exchanges.length)),
      includeTags: [...saved.includeTags], excludeTags: [...saved.excludeTags],
    }))
    setError(null)
  }, [saved, setFilters])

  useEffect(() => {
    if (!username || !hydrated || restoredUser.current === username) return
    restoredUser.current = username
    restore()
  }, [username, hydrated, restore])

  const save = async () => {
    if (!username || !hydrated || saving) return
    const defaults = normalizeDiscoveryDefaults(filters)!
    const preferences = { screener_discovery_filters: defaults }
    const requestId = ++request.current
    const isCurrent = () => currentUser.current === username && request.current === requestId
    setSaving(true)
    setError(null)
    try {
      const result = await updateAccountPreferences(preferences)
      if (!isCurrent()) return
      if (!result.ok) throw new Error(result.error || 'Could not save discovery filters. Please try again.')
      client.setQueryData<User | null>(['auth', 'me'], (current) => current?.username === username
        ? { ...current, preferences: { ...current.preferences, ...preferences } }
        : current)
    } catch (cause) {
      if (isCurrent()) setError(cause instanceof Error ? cause.message : 'Could not save discovery filters. Please try again.')
    } finally {
      if (isCurrent()) setSaving(false)
    }
  }

  return { hasSaved: saved !== null, saving: saving || !username || !hydrated, error, onSave: () => { void save() }, onRestore: restore }
}
